import styled, { keyframes, css } from 'styled-components';
import Colors, { hexToRgbA } from './colors';

export const fade = (from, to) => keyframes`
  0% {
    opacity: ${from};
  }

  100% {
    opacity: ${to};
  }
`;

export const FlexDiv = styled.div`
  display: flex;
  ${props => ('alignItems' in props ? `align-items: ${props.alignItems};` : '')}
  ${props => ('justifyContent' in props ? `justify-content: ${props.justifyContent};` : '')}
`;

export const FullContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
`;

export const FullContentSection = styled.section`
  flex: 1;
  display: flex;
  ${props => (props.background ? `background: ${props.background}` : '')}
`;

export const LoadingWrapper = styled.div`
  width: 100%;
  ${props => (props.height !== 'auto' ? `height: ${props.height || 100}vh;` : `height: auto;`)}
  display: flex;
  justify-content: center;
  align-items: center;

  ${props =>
    props.float
      ? `
    position: absolute;
    height: 100%;
    z-index: 9999;
    background: rgba(255, 255, 255, 0.8);
    top: 0;
    left: 0;
  `
      : ''}

  .wavey {
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    overflow: hidden;
  }
`;

export const LoadingContainer = styled.div`
  display: flex;
  width: 100%;
  justify-content: center;
  align-items: center;
`;

export const DraftEditorWrapper = styled.div`
  .rdw-option-wrapper {
    width: 35px;
    height: 32px;
  }
`;

export const ActionButton = styled.button`
  background-color: ${Colors.mainOrange};
  border: 1px solid ${Colors.mainOrange};
  padding: 10px 20px;
  border: 0;
  color: ${Colors.mainWhite};
  border-radius: 4px;

  &.btn {
    &:hover,
    &:active,
    &:focus {
      color: #fff;
    }
  }
`;

export const NotAllowedLink = styled.a`
  cursor: not-allowed;
`;

export const HigherPlanTag = styled.span`
  line-height: 17px;
  white-space: nowrap;
  border-radius: 0.35em;
  font-size: ${props => (props.fontSize ? props.fontSize : '1.1')}rem;
  font-weight: bold;
  display: inline;
  padding: 2px 6px;
  color: ${Colors.mainOrange};
  background-color: ${Colors.mainWhite};
  border: 1px solid ${Colors.mainOrange};
  max-height: 21px;

  &:before {
    content: 'Upgrade';
    text-transform: uppercase;
  }
`;

export const FormWrapper = styled.div`
  overflow: hidden;
  border-radius: 20px;
  width: auto;
  background-color: #fff;
  padding: 40px;
`;

export const FlexiRow = styled(FlexDiv)`
  flex-wrap: ${props => props.flexWrap || 'wrap'};
  &.row {
    &:before,
    &:after {
      content: normal;
    }

    &.centered {
      justify-content: center;
      align-items: center;
    }
  }
`;

export const RowDivision = styled.div`
  padding: 10px 0;
`;

export const BaseButton = styled.button`
  padding: 8px 30px;
  border-radius: 5px;
  border: 1px solid ${props => props.borderColor || props.primaryColor || Colors.mainOrange};
  background-color: ${props => props.primaryColor || Colors.mainOrange};
  color: ${props => props.textColor || Colors.mainWhite};

  &:hover {
    border-color: ${props =>
      hexToRgbA(props.borderColor || props.primaryColor || Colors.mainOrange, 0.85)};
    background-color: ${props => hexToRgbA(props.primaryColor || Colors.mainOrange, 0.85)};
  }

  &:disabled {
    animation: ${fade(1, 0.5)} 0.5s normal forwards cubic-bezier(0.4, 0, 0.2, 1);
  }

  &.btn {
    &:hover {
      ${props => (props.hoverOpacity ? `opacity: ${props.hoverOpacity};` : '')}
    }

    &:hover,
    &:focus,
    &:active {
      color: ${props => props.textColor || Colors.mainWhite};
    }

    &.naked {
      color: #333;
      background-color: #fff;
      border-color: #fff;

      &:hover,
      &:focus,
      &:active {
        background-color: #f4f4f4;
        border-color: #f4f4f4;
      }
    }
  }
`;

export const LandingBtnWrapper = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const commonButton = css`
  padding: 13px 50px;
  border-radius: 30px;
  background: ${Colors.mainOrange};
  color: ${props => (props.textcolor ? props.textcolor : Colors.mainWhite)};
  font-weight: 800;
  letter-spacing: 1px;
  border: 1px solid #fff;

  @media (max-width: 991px) {
    padding: 13px 50px;
  }

  &:disabled {
    animation: ${fade(1, 0.5)} 0.5s normal forwards cubic-bezier(0.4, 0, 0.2, 1);
  }
`;

export const BaseLandingButton = css`
  ${commonButton};
  background: ${props =>
    props.inverted ? props.textcolor || Colors.mainOrange : Colors.mainWhite};
  color: ${props => (props.inverted ? Colors.mainWhite : props.textcolor || Colors.mainOrange)};
  transition: all 0.8s cubic-bezier(0.2, 0.8, 0.2, 1) 0s;

  @media (max-width: 991px) {
    padding: 15px 50px;
  }

  &:hover,
  &:active,
  &:visited {
    color: ${props => (props.inverted ? Colors.mainWhite : props.textcolor || Colors.mainOrange)};
  }

  &:hover {
    &:not([disabled]) {
      transform: translateY(-3px);
    }
  }
`;

export const Select = styled.select`
  appearance: none;
  background-image: url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAEAAAABACAMAAACdt4HsAAAAJFBMVEUAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAD///+m5iRVAAAACnRSTlMADQ9XWHx9fn+A42dm/AAAAE9JREFUeNrtzDkSgEAQw0BzY/7/YEJHW7XMJATqXBIAAH+37iPboil+Bg5NcrEPD/rW4dQnLvbhVp9D+tbhUonTFzl953CrwemLfAsAgHkv2uIHTO2ldWkAAAAASUVORK5CYII=');
  background-repeat: no-repeat;
  background-position: right center;
  background-size: 25px;
  padding-right: 35px !important;
`;

export const ToggleLabel = styled.span`
  font-size: 16px;
  font-weight: 800;
  margin-left: 0.5em;
  width: 100%;
`;

export const ToggleWrapper = styled(FlexDiv)`
  align-items: center;
`;

export const NoAccess = styled.div`
  height: 90vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-size: 1.6rem;

  .upgrade {
    width: 680px;
    box-shadow: rgba(228, 228, 228, 0.3) 0px 11px 20px;
  }
`;

export const InputLabel = styled.div`
  font-size: ${({ fontSize }) => (fontSize ? fontSize : '1.6rem')};
  font-weight: 700;
  margin: 0px 0px 10px;
`;

export const InputText = styled.input`
  &[type='text'],
  &[type='email'],
  &[type='password'] {
    font-size: 1.6rem;
    border-radius: 4px;
    margin-bottom: 0px;
  }

  ${props => (props.errored ? 'border: 1px solid #F44336 !important;' : '')}
`;

export const InputTextArea = styled.textarea`
  font-size: 1.6rem;
  border-radius: 4px;
  margin-bottom: 0px;
  height: 60px;
  min-height: 60px;
  max-height: 100px;
  resize: vertical;

  ${props => (props.errored ? 'border: 1px solid #F44336 !important;' : '')}
`;
